import React from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'
import { CCQ_DECIDING_STATE, ccqCriterionTitle, decidingCriteria, type CcqCard } from './ccqResultView'
import { CCQ_SUMMARY_PREFIX, CCQ_VIEW, ccqMovedFrom, ccqSummaryMore } from './ccqRunCopy'
import { CcqActionTagSlot } from './CcqActionTag'
import { useCcqClientView } from './useCcqClientView'
import { useCcqWorkspace } from './useCcqWorkspace'
import styles from './CcqGroupCard.module.scss'

interface CcqClientRowProps {
  result: CrossClientQueryResult
  // The row's card.
  card: CcqCard
  isSelected: boolean
  showsEmail: boolean
  checkbox: { checked: boolean; onChange: () => void } | null
  onView: (result: CrossClientQueryResult) => void
  hasWideColumns?: boolean
}

const SUMMARY_NAMED = 2

interface DecidingSummaryProps {
  prefix: string
  criteria: string[]
}

// Only the criteria truncate: the prefix and the count always show.
const DecidingSummary: React.FC<DecidingSummaryProps> = ({ prefix, criteria }) => {
  const more = criteria.length > SUMMARY_NAMED ? ccqSummaryMore(criteria.length - SUMMARY_NAMED) : ''
  return (
    <span className='d-flex mw-0'>
      <span className='zocks-text-gray-600 flex-shrink-0 me-1'>{prefix}</span>
      <span className='zocks-text-gray-700 text-truncate'>{criteria.slice(0, SUMMARY_NAMED).join(', ')}</span>
      {more ? <span className='zocks-text-gray-600 flex-shrink-0'>{more}</span> : null}
    </span>
  )
}

export const CcqClientRow: React.FC<CcqClientRowProps> = ({ result, card, isSelected, showsEmail, checkbox, onView, hasWideColumns = false }) => {
  const { movedFrom } = useCcqWorkspace()
  const origin = movedFrom(result)
  const client = useCcqClientView(result)
  const status = client.card ?? card
  const decidingState = CCQ_DECIDING_STATE[status]
  const summaryCriteria = decidingCriteria(client.criteria, status).map((criterion) => ccqCriterionTitle(criterion.clause))

  return (
    // Insets line up with the See all row and card headings, and with CcqFullList's toolbar when there's a checkbox.
    <li className={checkbox ? 'px-2' : 'pe-4 zocks-pl-1.75'}>
      <div
        className={classNames(
          styles.row,
          'd-flex align-items-center gap-3 zocks-py-0.75 pe-3 rounded-3',
          checkbox ? 'ps-2' : 'ps-3',
          isSelected ? styles.rowSelected : styles.rowIdle,
        )}
        aria-current={isSelected ? 'true' : undefined}
        // A mouse shortcut; "View" stays the keyboard and screen reader control.
        onClick={() => onView(result)}
      >
        {checkbox ? (
          <Form.Check
            type='checkbox'
            className={classNames(styles.checkbox, 'm-0 flex-shrink-0')}
            checked={checkbox.checked}
            onChange={checkbox.onChange}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
            aria-label={`Select ${client.name}`}
          />
        ) : null}
        <div className={classNames('d-flex align-items-center flex-grow-1 mw-0', hasWideColumns ? 'zocks-gap-2' : 'gap-3')}>
          <div className={classNames(styles.name, 'd-flex flex-column flex-shrink-0')}>
            <span className='paragraph-small bold zocks-text-gray-900 text-truncate'>{client.name}</span>
            {showsEmail ? <span className='paragraph-small zocks-text-gray-600 text-truncate'>{client.email}</span> : null}
          </div>
          <span className='paragraph-small flex-grow-1 mw-0 text-nowrap' data-testid='ccq-row-summary'>
            {origin !== null ? (
              <span className='d-block zocks-text-gray-700 text-truncate'>{ccqMovedFrom(origin)}</span>
            ) : decidingState === undefined || summaryCriteria.length === 0 ? null : (
              <DecidingSummary prefix={CCQ_SUMMARY_PREFIX[decidingState]} criteria={summaryCriteria} />
            )}
          </span>
          <CcqActionTagSlot resultId={client.resultId} clientId={client.clientId} clientName={client.name} isAiChecking={client.isAiChecking} />
          <div className='d-flex justify-content-end flex-shrink-0'>
            <Button
              variant='link'
              className='button-small text-decoration-none zocks-text-gray-900 p-0 border-0'
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                onView(result)
              }}
              aria-label={`View ${client.name}`}
            >
              {CCQ_VIEW}
            </Button>
          </div>
        </div>
      </div>
    </li>
  )
}

// The text's line height (held by the &nbsp;) makes a loading row as tall as a loaded one.
const CcqSkeletonLine: React.FC<{ className: string }> = ({ className }) => (
  <span className='paragraph-small d-block text-nowrap'>
    <span className={classNames(styles.skeletonBar, 'skeleton d-inline-block align-middle rounded-1', className)} />
    &nbsp;
  </span>
)

interface CcqClientRowSkeletonProps {
  showsEmail: boolean
  hasCheckbox: boolean
  hasWideColumns?: boolean
}

// Mirrors CcqClientRow's insets and lines, so nothing moves when the row replaces it.
export const CcqClientRowSkeleton: React.FC<CcqClientRowSkeletonProps> = ({ showsEmail, hasCheckbox, hasWideColumns = false }) => (
  <li className={hasCheckbox ? 'px-2' : 'pe-4 zocks-pl-1.75'} aria-hidden='true'>
    <div className={classNames('d-flex align-items-center gap-3 zocks-py-0.75 pe-3', hasCheckbox ? 'ps-2' : 'ps-3')}>
      {hasCheckbox ? <span className={classNames(styles.skeletonCheckbox, 'skeleton flex-shrink-0 rounded-1')} /> : null}
      <div className={classNames('d-flex align-items-center flex-grow-1 mw-0', hasWideColumns ? 'zocks-gap-2' : 'gap-3')}>
        <div className={classNames(styles.name, 'd-flex flex-column flex-shrink-0')}>
          <CcqSkeletonLine className={styles.skeletonName} />
          {showsEmail ? <CcqSkeletonLine className={styles.skeletonEmail} /> : null}
        </div>
        <span className='flex-grow-1 mw-0'>
          <CcqSkeletonLine className={styles.skeletonReason} />
        </span>
      </div>
      <span className='button-small invisible'>{CCQ_VIEW}</span>
    </div>
  </li>
)
